import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    // Try JWT token first (account-based auth)
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Fallback to session ID for backward compatibility during migration
      const session = JSON.parse(localStorage.getItem('dripbattle_session') || '{}');
      if (session.id) {
        config.headers['X-Session-ID'] = session.id;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle 401 (token expiration)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and haven't retried yet, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          // Try to refresh the token
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.data?.token) {
            localStorage.setItem('auth_token', response.data.token);
            originalRequest.headers['Authorization'] = `Bearer ${response.data.token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear token
          localStorage.removeItem('auth_token');
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Submit a new avatar by Roblox username
 */
export async function submitAvatar(username) {
  try {
    const response = await api.post('/avatars/submit', { username });
    return response.data;
  } catch (error) {
    console.error('Submit avatar error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    throw error;
  }
}

/**
 * Fetch all avatars for voting
 */
export async function fetchAvatars() {
  try {
    const response = await api.get('/avatars');
    return response.data;
  } catch (error) {
    console.error('Fetch avatars error:', error);
    return [];
  }
}

/**
 * Submit a vote for an avatar
 */
export async function submitVote(avatarId, categoryId, isHot, voteMultiplier = 1) {
  try {
    const response = await api.post('/votes', {
      avatarId,
      categoryId,
      isHot,
      voteMultiplier,
    });
    return response.data;
  } catch (error) {
    console.error('Submit vote error:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    if (error.response?.data) {
      // Include the status code in the returned error object
      return {
        ...error.response.data,
        status: error.response.status
      };
    }
    return { success: false, error: error.message || 'Network error' };
  }
}

/**
 * Fetch leaderboard for a specific category
 */
export async function fetchLeaderboard(categoryId = 'overall') {
  try {
    const response = await api.get(`/leaderboard/${categoryId}`);
    return response.data;
  } catch (error) {
    console.error('Fetch leaderboard error:', error);
    return [];
  }
}

/**
 * Create a Stripe checkout session for buying votes
 */
export async function createCheckoutSession(packageId) {
  try {
    const response = await api.post('/payments/create-checkout', {
      packageId,
    });
    return response.data;
  } catch (error) {
    console.error('Create checkout error:', error);
    throw error;
  }
}

/**
 * Verify payment and add tokens to session
 */
export async function verifyPayment(stripeSessionId) {
  try {
    const response = await api.post('/payments/verify', {
      stripeSessionId,
    });
    return response.data;
  } catch (error) {
    console.error('Payment verification error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return { success: false, error: 'Verification failed' };
  }
}

/**
 * Check if user has already voted on an avatar's aura
 */
export async function checkIfVoted(avatarId) {
  try {
    const response = await api.get(`/check-vote/${avatarId}`);
    return response.data;
  } catch (error) {
    console.error('Check vote error:', error);
    return { hasVoted: false };
  }
}

/**
 * Check if user is on VPN
 */
export async function checkVPN() {
  try {
    const response = await api.get('/check-vpn');
    return response.data;
  } catch (error) {
    console.error('VPN check error:', error);
    return { isVPN: false };
  }
}

/**
 * Check daily claim status (without claiming)
 */
export async function checkDailyClaimStatus() {
  try {
    const response = await api.get('/check-daily-claim');
    return response.data;
  } catch (error) {
    console.error('Check daily claim status error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return { canClaim: false, status: 'error' };
  }
}

/**
 * Claim daily free tokens
 */
export async function claimDailyTokens() {
  try {
    const response = await api.post('/claim-daily-tokens');
    return response.data;
  } catch (error) {
    console.error('Claim daily tokens error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return { success: false, tokensEarned: 0 };
  }
}

/**
 * Get all avatar IDs the user has voted on for aura farming
 */
export async function getVotedAvatars() {
  try {
    const response = await api.get('/voted-avatars');
    return response.data;
  } catch (error) {
    console.error('Get voted avatars error:', error);
    return { votedAvatarIds: [] };
  }
}

/**
 * Get server-side token balance
 */
export async function getServerBalance() {
  try {
    const response = await api.get('/balance');
    return response.data;
  } catch (error) {
    console.error('Get server balance error:', error);
    return { balance: 0 };
  }
}

/**
 * Refresh avatar from Roblox API (authenticated users only)
 */
export async function refreshAvatar() {
  try {
    const response = await api.post('/avatars/refresh');
    return response.data;
  } catch (error) {
    console.error('Refresh avatar error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return { success: false, error: 'Failed to refresh avatar' };
  }
}

export default api;
